import { z } from 'zod';

/**
 * Date string validation (YYYY-MM-DD format)
 */
const dateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');

/**
 * Granularity for time series data
 */
export const GranularityEnum = z.enum(['day', 'week', 'month']);
export type Granularity = z.infer<typeof GranularityEnum>;

/**
 * Analytics filter schema for date range queries
 */
export const AnalyticsFilterSchema = z.object({
  startDate: dateStringSchema,
  endDate: dateStringSchema,
  granularity: GranularityEnum.optional().default('day'),
});

export type AnalyticsFilter = z.infer<typeof AnalyticsFilterSchema>;

/**
 * Booking source filter for online/offline analytics
 */
export const BookingSourceFilterEnum = z.enum([
  'ALL',
  'ONLINE',
  'OFFLINE',
  'WEB',
  'MOBILE',
  'WALK_IN',
  'ADMIN',
]);
export type BookingSourceFilter = z.infer<typeof BookingSourceFilterEnum>;

/**
 * Extended analytics filter with booking source
 */
export const AnalyticsFilterWithSourceSchema = AnalyticsFilterSchema.extend({
  bookingSource: BookingSourceFilterEnum.optional().default('ALL'),
});
export type AnalyticsFilterWithSource = z.infer<typeof AnalyticsFilterWithSourceSchema>;

/**
 * Time series data point
 */
export const TimeSeriesDataPointSchema = z.object({
  period: z.string(),
  value: z.number(),
  count: z.number().optional(),
});

export type TimeSeriesDataPoint = z.infer<typeof TimeSeriesDataPointSchema>;

/**
 * Admin overview statistics
 */
export const AdminOverviewStatsSchema = z.object({
  totalRevenue: z.number(),
  totalBookings: z.number(),
  activeVenues: z.number(),
  totalUsers: z.number(),
  revenueTrend: z.number(), // % change vs previous period
  bookingsTrend: z.number(),
});

export type AdminOverviewStats = z.infer<typeof AdminOverviewStatsSchema>;

/**
 * Vendor overview statistics
 */
export const VendorOverviewStatsSchema = z.object({
  totalRevenue: z.number(),
  totalBookings: z.number(),
  avgBookingValue: z.number(),
  occupancyRate: z.number(),
  revenueTrend: z.number(),
  bookingsTrend: z.number(),
});

export type VendorOverviewStats = z.infer<typeof VendorOverviewStatsSchema>;

/**
 * Venue type distribution
 */
export const VenueTypeDistributionSchema = z.object({
  type: z.string(),
  count: z.number(),
  percentage: z.number(),
});

export type VenueTypeDistribution = z.infer<typeof VenueTypeDistributionSchema>;

/**
 * Top venue by revenue
 */
export const TopVenueSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  city: z.string(),
  totalRevenue: z.number(),
  totalBookings: z.number(),
  averageRating: z.number().nullish(),
});

export type TopVenue = z.infer<typeof TopVenueSchema>;

/**
 * Venue revenue breakdown (for vendor analytics)
 */
export const VenueRevenueSchema = z.object({
  venueId: z.string(),
  venueName: z.string(),
  revenue: z.number(),
  bookings: z.number(),
});

export type VenueRevenue = z.infer<typeof VenueRevenueSchema>;

/**
 * Revenue breakdown by inventory item
 */
export const ItemRevenueSchema = z.object({
  itemId: z.string(),
  itemName: z.string(),
  category: z.string(),
  revenue: z.number(),
  bookingCount: z.number(),
  percentage: z.number(),
});

export type ItemRevenue = z.infer<typeof ItemRevenueSchema>;

/**
 * Payment status distribution
 */
export const PaymentStatusDistributionSchema = z.object({
  status: z.string(),
  count: z.number(),
  amount: z.number(),
  percentage: z.number(),
});

export type PaymentStatusDistribution = z.infer<typeof PaymentStatusDistributionSchema>;

/**
 * Booking status distribution
 */
export const BookingStatusDistributionSchema = z.object({
  status: z.string(),
  count: z.number(),
  percentage: z.number(),
});

export type BookingStatusDistribution = z.infer<typeof BookingStatusDistributionSchema>;

/**
 * User growth statistics
 */
export const UserGrowthDataPointSchema = z.object({
  period: z.string(),
  newUsers: z.number(),
  totalUsers: z.number(),
});

export type UserGrowthDataPoint = z.infer<typeof UserGrowthDataPointSchema>;

/**
 * Coupon usage statistics
 */
export const CouponUsageStatsSchema = z.object({
  totalCouponsUsed: z.number(),
  totalDiscountGiven: z.number(),
  topCoupons: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      usageCount: z.number(),
      totalDiscount: z.number(),
    })
  ),
});

export type CouponUsageStats = z.infer<typeof CouponUsageStatsSchema>;

export const VendorDiscountSummarySchema = z.object({
  couponDiscountTotal: z.number(),
  couponDiscountCount: z.number(),
  walkInDiscountTotal: z.number(),
  walkInDiscountCount: z.number(),
  totalDiscountAmount: z.number(),
  grossRevenue: z.number(),
  topCoupons: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      usageCount: z.number(),
      totalDiscount: z.number(),
    })
  ),
  previousPeriod: z
    .object({
      couponDiscountTotal: z.number(),
      walkInDiscountTotal: z.number(),
      totalDiscountAmount: z.number(),
    })
    .optional(),
  period: z.object({ startDate: z.string(), endDate: z.string() }),
});
export type VendorDiscountSummary = z.infer<typeof VendorDiscountSummarySchema>;

// ============================================
// ADMIN ANALYTICS RESPONSE TYPES
// ============================================

export interface AdminAnalyticsOverviewResponse {
  stats: AdminOverviewStats;
  period: {
    startDate: string;
    endDate: string;
  };
}

export interface AdminRevenueTrendResponse {
  data: TimeSeriesDataPoint[];
  total: number;
  period: {
    startDate: string;
    endDate: string;
    granularity: Granularity;
  };
}

export interface AdminBookingsTrendResponse {
  data: TimeSeriesDataPoint[];
  total: number;
  period: {
    startDate: string;
    endDate: string;
    granularity: Granularity;
  };
}

export interface AdminVenueTypesResponse {
  data: VenueTypeDistribution[];
  total: number;
}

export interface AdminUserGrowthResponse {
  data: UserGrowthDataPoint[];
  period: {
    startDate: string;
    endDate: string;
    granularity: Granularity;
  };
}

export interface AdminTopVenuesResponse {
  data: TopVenue[];
  period: {
    startDate: string;
    endDate: string;
  };
}

// ============================================
// VENDOR ANALYTICS RESPONSE TYPES
// ============================================

export interface VendorAnalyticsOverviewResponse {
  stats: VendorOverviewStats;
  period: {
    startDate: string;
    endDate: string;
  };
}

export interface VendorVenueRevenueResponse {
  data: VenueRevenue[];
  total: number;
  period: {
    startDate: string;
    endDate: string;
  };
}

export interface VendorBookingsTrendResponse {
  data: TimeSeriesDataPoint[];
  total: number;
  period: {
    startDate: string;
    endDate: string;
    granularity: Granularity;
  };
}

export interface VendorOccupancyTrendResponse {
  data: TimeSeriesDataPoint[];
  averageOccupancy: number;
  period: {
    startDate: string;
    endDate: string;
    granularity: Granularity;
  };
}

export interface VendorPaymentStatusResponse {
  data: PaymentStatusDistribution[];
  period: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Source breakdown for daily sales
 */
export const SourceBreakdownSchema = z.object({
  onlineGrossSales: z.number(),
  offlineGrossSales: z.number(),
  onlineBookings: z.number(),
  offlineBookings: z.number(),
  offlinePaymentMethods: z.array(
    z.object({
      method: z.string(),
      count: z.number(),
      amount: z.number(),
    })
  ),
});
export type SourceBreakdown = z.infer<typeof SourceBreakdownSchema>;

// ============================================
// DAILY SALES SCHEMAS
// ============================================

/**
 * Venue breakdown for daily sales (vendor view)
 */
export const VenueBreakdownSchema = z.object({
  venueId: z.string(),
  venueName: z.string(),
  grossSales: z.number(),
  commissionAmount: z.number(),
  tcsAmount: z.number(),
  netPayout: z.number(),
  bookingsCount: z.number(),
});

export type VenueBreakdown = z.infer<typeof VenueBreakdownSchema>;

/**
 * Vendor daily sales response
 */
export const VendorDailySalesSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  grossSales: z.number(),
  bookingsScheduledToday: z.number(),
  bookingsCreatedToday: z.number(),
  bookingsCompletedToday: z.number(),
  commissionRate: z.number(),
  commissionAmount: z.number(),
  tcsRate: z.number(),
  tcsAmount: z.number(),
  netPayout: z.number(),
  byVenue: z.array(VenueBreakdownSchema),
  sourceBreakdown: SourceBreakdownSchema.optional(),
});

export type VendorDailySales = z.infer<typeof VendorDailySalesSchema>;

/**
 * Admin daily sales totals
 */
export const AdminDailyTotalsSchema = z.object({
  grossSales: z.number(),
  commissionEarned: z.number(),
  tcsCollected: z.number(),
  netPayoutToVendors: z.number(),
  bookingsScheduledToday: z.number(),
  bookingsCreatedToday: z.number(),
  bookingsCompletedToday: z.number(),
  vendorCount: z.number(),
  venueCount: z.number(),
  sourceBreakdown: SourceBreakdownSchema.optional(),
});

export type AdminDailyTotals = z.infer<typeof AdminDailyTotalsSchema>;

/**
 * Breakdown item (vendor or venue) for admin
 */
export const AdminBreakdownItemSchema = z.object({
  vendorId: z.string().optional(),
  vendorName: z.string().optional(),
  venueId: z.string().optional(),
  venueName: z.string().optional(),
  grossSales: z.number(),
  commissionEarned: z.number(),
  tcsCollected: z.number(),
  netPayout: z.number(),
  bookingsCount: z.number(),
});

export type AdminBreakdownItem = z.infer<typeof AdminBreakdownItemSchema>;

/**
 * Admin daily sales response
 */
export const AdminDailySalesSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  totals: AdminDailyTotalsSchema,
  breakdown: z.array(AdminBreakdownItemSchema),
});

export type AdminDailySales = z.infer<typeof AdminDailySalesSchema>;

// ============================================
// OFFLINE SALES ANALYTICS SCHEMAS
// ============================================

/**
 * Payment method breakdown for offline sales
 */
export const PaymentMethodBreakdownItemSchema = z.object({
  method: z.string(),
  count: z.number(),
  amount: z.number(),
  percentage: z.number(),
});
export type PaymentMethodBreakdownItem = z.infer<typeof PaymentMethodBreakdownItemSchema>;

/**
 * Booking source distribution (admin pie chart)
 */
export const BookingSourceDistributionSchema = z.object({
  source: z.string(),
  count: z.number(),
  revenue: z.number(),
  percentage: z.number(),
});
export type BookingSourceDistribution = z.infer<typeof BookingSourceDistributionSchema>;

/**
 * Offline revenue stats (vendor + admin)
 */
export const OfflineRevenueStatsSchema = z.object({
  offlineRevenue: z.number(),
  onlineRevenue: z.number(),
  totalRevenue: z.number(),
  offlineBookings: z.number(),
  onlineBookings: z.number(),
  totalBookings: z.number(),
  offlinePercentage: z.number(),
  paymentMethodBreakdown: z.array(PaymentMethodBreakdownItemSchema),
});
export type OfflineRevenueStats = z.infer<typeof OfflineRevenueStatsSchema>;

/**
 * Admin earning potential (commission on offline revenue)
 */
export const EarningPotentialSchema = z.object({
  offlineRevenue: z.number(),
  commissionRate: z.number(),
  potentialCommission: z.number(),
  potentialGstOnCommission: z.number(),
  totalEarningPotential: z.number(),
});
export type EarningPotential = z.infer<typeof EarningPotentialSchema>;

/**
 * Vendor tax summary for GST filing
 */
export const VendorTaxSummaryItemSchema = z.object({
  period: z.string(),
  onlineSales: z.number(),
  offlineSales: z.number(),
  totalSales: z.number(),
  totalGstCollected: z.number(),
  cgst: z.number(),
  sgst: z.number(),
  igst: z.number(),
  invoiceCount: z.number(),
  onlineInvoiceCount: z.number(),
  offlineInvoiceCount: z.number(),
});
export type VendorTaxSummaryItem = z.infer<typeof VendorTaxSummaryItemSchema>;

// ============================================
// EXPANSION SCHEMAS
// ============================================

/**
 * [EXP-1] Revenue heatmap data point (hour × day × source)
 */
export const RevenueHeatmapDataPointSchema = z.object({
  hour: z.number().min(0).max(23),
  dayOfWeek: z.number().min(0).max(6),
  source: z.string(),
  revenue: z.number(),
  bookings: z.number(),
});
export type RevenueHeatmapDataPoint = z.infer<typeof RevenueHeatmapDataPointSchema>;

/**
 * [EXP-2] Guest-to-online conversion stats
 */
export const GuestConversionStatsSchema = z.object({
  totalGuestCustomers: z.number(),
  convertedToRegistered: z.number(),
  conversionRate: z.number(),
  convertedOnlineBookings: z.number(),
  convertedOnlineRevenue: z.number(),
});
export type GuestConversionStats = z.infer<typeof GuestConversionStatsSchema>;

/**
 * [EXP-3] GSTR-1 export row (matches GST portal format)
 */
export const Gstr1ExportRowSchema = z.object({
  gstin: z.string(),
  invoiceNumber: z.string(),
  invoiceDate: z.string(),
  invoiceValue: z.number(),
  taxableValue: z.number(),
  cgst: z.number(),
  sgst: z.number(),
  igst: z.number(),
  placeOfSupply: z.string(),
  hsnSac: z.string(),
  notes: z.string().optional(),
});
export type Gstr1ExportRow = z.infer<typeof Gstr1ExportRowSchema>;

/**
 * [EXP-4] Vendor revenue target with progress
 */
export const VendorRevenueTargetSchema = z.object({
  id: z.string().optional(),
  vendorId: z.string(),
  venueId: z.string().optional(),
  month: z.number().min(1).max(12),
  year: z.number(),
  targetAmount: z.number().positive(),
  currentRevenue: z.number().optional(),
  onlineRevenue: z.number().optional(),
  offlineRevenue: z.number().optional(),
  progressPercent: z.number().optional(),
});
export type VendorRevenueTarget = z.infer<typeof VendorRevenueTargetSchema>;

export const SetRevenueTargetSchema = z.object({
  month: z.number().min(1).max(12),
  year: z.number().min(2024).max(2030),
  targetAmount: z.number().positive().max(10000000), // Max 1 crore
  venueId: z.string().uuid().optional(),
});
export type SetRevenueTarget = z.infer<typeof SetRevenueTargetSchema>;

/**
 * [EXP-5] Smart auto-generated insight
 */
export const SmartInsightSchema = z.object({
  type: z.enum(['trend', 'comparison', 'highlight']),
  title: z.string(),
  description: z.string(),
  value: z.number().optional(),
  changePercent: z.number().optional(),
});
export type SmartInsight = z.infer<typeof SmartInsightSchema>;

/**
 * [EXP-7] Vendor adoption leaderboard item
 */
export const VendorAdoptionItemSchema = z.object({
  vendorId: z.string(),
  vendorName: z.string(),
  totalRevenue: z.number(),
  onlineRevenue: z.number(),
  offlineRevenue: z.number(),
  onlinePercent: z.number(),
  conversionCount: z.number(),
});
export type VendorAdoptionItem = z.infer<typeof VendorAdoptionItemSchema>;

// ============================================
// OFFLINE ANALYTICS RESPONSE TYPES
// ============================================

export interface VendorOfflineRevenueResponse {
  stats: OfflineRevenueStats;
  period: { startDate: string; endDate: string };
}

export interface AdminBookingSourceDistributionResponse {
  data: BookingSourceDistribution[];
  period: { startDate: string; endDate: string };
}

export interface AdminEarningPotentialResponse {
  data: EarningPotential;
  period: { startDate: string; endDate: string };
}

export interface VendorTaxSummaryResponse {
  data: VendorTaxSummaryItem[];
  year: number;
  quarter?: number;
}

export interface VendorRevenueHeatmapResponse {
  data: RevenueHeatmapDataPoint[];
  period: { startDate: string; endDate: string };
}

export interface GuestConversionResponse {
  stats: GuestConversionStats;
  period: { startDate: string; endDate: string };
}

export interface VendorRevenueTargetResponse {
  target: VendorRevenueTarget | null;
}

export interface VendorInsightsResponse {
  insights: SmartInsight[];
  period: { startDate: string; endDate: string };
}

export interface AdminVendorAdoptionResponse {
  data: VendorAdoptionItem[];
  period: { startDate: string; endDate: string };
}

// ============================================
// VENDOR USER ANALYTICS
// ============================================

export * from './vendor-user-analytics';
